import { useState, useEffect } from "react";
import api from "../services/api";

// Toute l'IA d'ExecPro360 passe par Mistral AI (02/10/2026) : plus de choix
// de fournisseur, ni de clés propres à l'abonné.
const AGENT_INFO = { name: "Mistral AI", icon: "🇫🇷", color: "#8b5cf6" };

const STAGES = {
  prospection: "Prospection",
  qualification: "Qualification",
  prise_de_contact: "Prise de contact",
  suivi: "Suivi",
};

const STATUS_COLORS = {
  new: "#3b82f6",
  contacted: "#f59e0b",
  qualified: "#10b981",
  ignored: "#6b7280",
  converted: "#1d9e75",
};

// ── Onglet 1 : Créer une campagne ──────────────────────────────
function CreateCampaignTab({ onCreated, currentCompanyId }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    target_sector: "",
    target_size: "pme",
    target_country: "France",
    target_role: "",
    leads_count: 20,
    follow_up_sequence: false,
    follow_up_days: 3,
    budget_max: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Inclus dans le forfait (quotas mensuels : 10 campagnes, 50 emails générés)
  const estimateCost = () => "Inclus dans votre forfait";

  const handleSubmit = async () => {
    if (!form.name.trim()) { setMsg("Nom de la campagne requis."); return; }
    if (!currentCompanyId) { setMsg("Erreur : aucune société active sélectionnée."); return; }
    setSaving(true);
    setMsg("");
    try {
      const payload = {
        company_id: currentCompanyId,
        name: form.name,
        target_profile: {
          sector: form.target_sector,
          size: form.target_size,
          country: form.target_country,
          role: form.target_role,
          budget: form.budget_max,
        },
        ai_provider: "mistral",
      };
      const camp = await api.prospection.createCampaign(payload);
      setMsg("Campagne créée !");
      onCreated(camp);
    } catch (e) {
      setMsg("Erreur : " + (e.message || "impossible de créer la campagne"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <div style={{ background: "var(--card-bg)", borderRadius: 12, border: "1px solid var(--border-color)", padding: 24, marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 20px", color: "var(--text-primary)", fontSize: 16 }}>Profil cible</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Nom de la campagne *</label>
            <input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex : Cabinets comptables Paris Q3" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Description / objectif</label>
            <textarea style={{ ...inputStyle, height: 68, resize: "vertical" }} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Décrivez vos objectifs de prospection…" />
          </div>
          <div>
            <label style={labelStyle}>Secteur d'activité</label>
            <input style={inputStyle} value={form.target_sector} onChange={e => set("target_sector", e.target.value)} placeholder="Ex : Comptabilité, SaaS, Industrie" />
          </div>
          <div>
            <label style={labelStyle}>Taille d'entreprise</label>
            <select style={inputStyle} value={form.target_size} onChange={e => set("target_size", e.target.value)}>
              <option value="startup">Startup (1–10)</option>
              <option value="pme">PME (10–250)</option>
              <option value="eti">ETI (250–5000)</option>
              <option value="grand_groupe">Grand groupe (5000+)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Pays cible</label>
            <input style={inputStyle} value={form.target_country} onChange={e => set("target_country", e.target.value)} placeholder="France, Belgique…" />
          </div>
          <div>
            <label style={labelStyle}>Fonction / rôle visé</label>
            <input style={inputStyle} value={form.target_role} onChange={e => set("target_role", e.target.value)} placeholder="Ex : DAF, PDG, Responsable achats" />
          </div>
        </div>
      </div>

      <div style={{ background: "var(--card-bg)", borderRadius: 12, border: "1px solid var(--border-color)", padding: 24, marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 8px", color: "var(--text-primary)", fontSize: 16 }}>Agent IA</h3>
        <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
          {AGENT_INFO.icon} <strong>{AGENT_INFO.name}</strong>, société française — inclus dans votre forfait (10 campagnes et 50 emails générés par mois). Aucune clé à fournir.
        </div>
      </div>

      <div style={{ background: "var(--card-bg)", borderRadius: 12, border: "1px solid var(--border-color)", padding: 24, marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 16px", color: "var(--text-primary)", fontSize: 16 }}>Paramètres</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div>
            <label style={labelStyle}>Nombre de leads</label>
            <input style={inputStyle} type="number" min="5" max="200" value={form.leads_count} onChange={e => set("leads_count", parseInt(e.target.value) || 20)} />
          </div>
          <div>
            <label style={labelStyle}>Budget max (€)</label>
            <input style={inputStyle} type="number" min="0" step="0.5" value={form.budget_max} onChange={e => set("budget_max", e.target.value)} placeholder="Pas de limite" />
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            <div style={{ background: "rgba(29,158,117,0.1)", border: "1px solid rgba(29,158,117,0.25)", borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4 }}>Coût estimé</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1d9e75" }}>{estimateCost()}</div>
            </div>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={form.follow_up_sequence} onChange={e => set("follow_up_sequence", e.target.checked)} />
            <span style={{ fontSize: 13, color: "var(--text-primary)" }}>Générer une séquence de relances automatiques</span>
          </label>
          {form.follow_up_sequence && (
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Intervalle :</span>
              <input style={{ ...inputStyle, width: 80 }} type="number" min="1" max="30" value={form.follow_up_days} onChange={e => set("follow_up_days", parseInt(e.target.value) || 3)} />
              <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>jours</span>
            </div>
          )}
        </div>
      </div>

      {msg && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 13,
          background: msg.startsWith("Erreur") ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)",
          color: msg.startsWith("Erreur") ? "#ef4444" : "#10b981",
          border: `1px solid ${msg.startsWith("Erreur") ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}` }}>
          {msg}
        </div>
      )}

      <button onClick={handleSubmit} disabled={saving} style={{ ...btnPrimary, width: "100%", padding: "12px 0", fontSize: 14 }}>
        {saving ? "Création…" : "🚀 Créer la campagne et générer les leads"}
      </button>
    </div>
  );
}

// ── Onglet 2 : Leads générés ───────────────────────────────────
function LeadsTab({ currentCompanyId, onNavigate }) {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [emailContent, setEmailContent] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!currentCompanyId) return;
    api.prospection.campaigns(currentCompanyId).then(data => {
      setCampaigns(data || []);
      if (data && data.length > 0) setSelectedCampaign(data[0].id);
    }).catch(() => {});
  }, [currentCompanyId]);

  useEffect(() => {
    if (!selectedCampaign) return;
    setLoading(true);
    api.prospection.leads(selectedCampaign).then(data => setLeads(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [selectedCampaign]);

  const handleGenerate = async () => {
    if (!selectedCampaign) return;
    setGenerating(true);
    try {
      const result = await api.prospection.generate(selectedCampaign);
      const fresh = await api.prospection.leads(selectedCampaign);
      setLeads(fresh || []);
      alert(`${result.count || 0} leads générés par ${result.provider || "IA"} !`);
    } catch (e) {
      alert("Erreur génération : " + (e.message || "échec"));
    } finally {
      setGenerating(false);
    }
  };

  const handleStatus = async (leadId, status) => {
    try {
      await api.prospection.leadStatus(leadId, status);
      setLeads(ls => ls.map(l => l.id === leadId ? { ...l, status } : l));
    } catch {}
  };

  const handleToCrm = async (leadId) => {
    try {
      await api.prospection.leadToCrm(leadId, currentCompanyId);
      handleStatus(leadId, "converted");
      if (onNavigate) {
        onNavigate("crm");
      } else {
        alert("Lead converti en contact CRM !");
      }
    } catch (e) {
      alert("Erreur : " + (e.message || "échec"));
    }
  };

  const handleEmail = async (lead) => {
    setEmailModal(lead);
    setEmailContent("");
    setEmailLoading(true);
    try {
      const result = await api.prospection.generateEmail(lead.id);
      setEmailContent(result.email || "");
    } catch (e) {
      setEmailContent("Erreur : " + (e.message || "échec génération email"));
    } finally {
      setEmailLoading(false);
    }
  };

  const campaign = campaigns.find(c => c.id === selectedCampaign);

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <select style={{ ...inputStyle, maxWidth: 280 }} value={selectedCampaign || ""} onChange={e => setSelectedCampaign(parseInt(e.target.value))}>
          {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {campaign && (
          <span style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--bg-secondary)", padding: "4px 10px", borderRadius: 20 }}>
            {AGENT_INFO.icon} {AGENT_INFO.name}
          </span>
        )}
        <button onClick={handleGenerate} disabled={generating || !selectedCampaign} style={{ ...btnPrimary, marginLeft: "auto" }}>
          {generating ? "Génération…" : "⚡ Générer plus de leads"}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement des leads…</div>
      ) : leads.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)", background: "var(--card-bg)", borderRadius: 12, border: "1px dashed var(--border-color)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🎯</div>
          <p>Aucun lead généré pour cette campagne.</p>
          <p style={{ fontSize: 12 }}>Cliquez sur "Générer plus de leads" pour démarrer.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {leads.map(lead => (
            <LeadCard key={lead.id} lead={lead} onStatus={handleStatus} onToCrm={handleToCrm} onEmail={handleEmail} />
          ))}
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div style={modalOverlay} onClick={() => setEmailModal(null)}>
          <div style={{ ...modalBox, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Email personnalisé — {emailModal.company_name || emailModal.contact_name}</h3>
              <span onClick={() => setEmailModal(null)} style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: 18 }}>×</span>
            </div>
            {emailLoading ? (
              <div style={{ textAlign: "center", padding: 30, color: "var(--text-muted)" }}>Génération par IA…</div>
            ) : (
              <>
                <textarea value={emailContent} onChange={e => setEmailContent(e.target.value)}
                  style={{ ...inputStyle, height: 280, fontFamily: "inherit", lineHeight: 1.6 }} />
                <div style={{ display: "flex", gap: 10, marginTop: 12, justifyContent: "flex-end" }}>
                  <button onClick={() => { navigator.clipboard.writeText(emailContent); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={btnSecondary}>{copied ? '✅ Copié !' : '📋 Copier'}</button>
                  <button onClick={() => setEmailModal(null)} style={btnPrimary}>Fermer</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LeadCard({ lead, onStatus, onToCrm, onEmail }) {
  const score = lead.relevance_score || 0;
  const provInfo = AGENT_INFO;
  const statusColor = STATUS_COLORS[lead.status] || "#6b7280";

  return (
    <div style={{ background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 12, padding: 16, display: "flex", gap: 14, alignItems: "flex-start" }}>
      <div style={{ width: 48, height: 48, borderRadius: 10, background: `${statusColor}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🏢</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          <span style={{ fontWeight: 700, color: "var(--text-primary)", fontSize: 14 }}>{lead.company_name || "—"}</span>
          {lead.contact_name && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>· {lead.contact_name}</span>}
          {lead.contact_role && <span style={{ fontSize: 11, color: "#3b82f6", background: "rgba(59,130,246,0.1)", padding: "1px 7px", borderRadius: 10 }}>{lead.contact_role}</span>}
          <span style={{ fontSize: 10, background: `${statusColor}20`, color: statusColor, padding: "2px 8px", borderRadius: 10, fontWeight: 600, marginLeft: "auto" }}>{lead.status || "new"}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 6 }}>
          {[lead.sector, lead.city, lead.country].filter(Boolean).join(" · ")}
        </div>
        {lead.ai_reasoning && (
          <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 8, lineHeight: 1.5 }}>
            "{lead.ai_reasoning}"
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Score */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ height: 6, width: 80, background: "var(--bg-secondary)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${score}%`, background: score > 70 ? "#10b981" : score > 40 ? "#f59e0b" : "#ef4444", borderRadius: 3, transition: "width 0.3s" }} />
            </div>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{score}%</span>
          </div>
          {provInfo && (
            <span style={{ fontSize: 10, background: `${provInfo.color}20`, color: provInfo.color, padding: "2px 8px", borderRadius: 10 }}>
              {provInfo.icon} {provInfo.name}
            </span>
          )}
          {/* Actions */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button onClick={() => onEmail(lead)} style={{ ...btnTiny, background: "rgba(59,130,246,0.1)", color: "#3b82f6" }}>✉️ Email IA</button>
            {lead.status !== "converted" && <button onClick={() => onToCrm(lead.id)} style={{ ...btnTiny, background: "rgba(29,158,117,0.1)", color: "#1d9e75" }}>➕ CRM</button>}
            {lead.status !== "ignored" && <button onClick={() => onStatus(lead.id, "ignored")} style={{ ...btnTiny, background: "rgba(107,114,128,0.1)", color: "#6b7280" }}>✕</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Onglet 3 : Comparaison des agents ─────────────────────────
// ── Composant principal ────────────────────────────────────────
const TABS = [
  { id: "create",     label: "🚀 Créer campagne" },
  { id: "leads",      label: "🎯 Leads générés" },
];

export default function ProspectionIA({ currentCompanyId, onNavigate }) {
  const [tab, setTab] = useState("create");
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCampaignCreated = (camp) => {
    setRefreshKey(k => k + 1);
    setTab("leads");
  };

  return (
    <div style={{ padding: "20px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>
            🤖 Prospection IA
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            Générez et qualifiez vos leads avec l'IA de Mistral AI (France)
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onNavigate && (
            <button onClick={() => onNavigate("crm")} style={{ ...btnSecondary, fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              🤝 Voir le CRM
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid var(--border-color)", paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "8px 16px", border: "none", borderRadius: "8px 8px 0 0", cursor: "pointer", fontSize: 13, fontWeight: 500,
            background: tab === t.id ? "var(--card-bg)" : "transparent",
            color: tab === t.id ? "var(--text-primary)" : "var(--text-muted)",
            borderBottom: tab === t.id ? "2px solid #1d9e75" : "2px solid transparent",
            transition: "all 0.15s",
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "create"     && <CreateCampaignTab onCreated={handleCampaignCreated} currentCompanyId={currentCompanyId} />}
      {tab === "leads"      && <LeadsTab key={refreshKey} currentCompanyId={currentCompanyId} onNavigate={onNavigate} />}
    </div>
  );
}

// ── Styles partagés ────────────────────────────────────────────
const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" };
const inputStyle = { width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid var(--border-color)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 13, boxSizing: "border-box", outline: "none" };
const btnPrimary = { padding: "8px 18px", borderRadius: 8, border: "none", background: "var(--g)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnSecondary = { padding: "7px 14px", borderRadius: 8, border: "1px solid var(--border-color)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 13, cursor: "pointer" };
const btnTiny = { padding: "4px 10px", borderRadius: 6, border: "none", fontSize: 11, fontWeight: 600, cursor: "pointer" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const modalBox = { background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 14, padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto" };
